import { useMutation, useQueryClient } from '@tanstack/react-query';
import { del, get } from '@/shared/api/api-client';

/**
 * Archive (soft-delete) a practice.
 *   DELETE /api/Practice/{id}
 *
 * Mirrors Flutter `deletePracticeById`. The row disappears from the active
 * list and shows up in the archived list — caller refetches both keys.
 */
export function useArchivePractice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (practiceId: string) => del<unknown>(`/api/Practice/${practiceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'practices-list'] });
    },
  });
}

/**
 * Restore an archived practice.
 *   GET /api/Practice/Unarchive/{id}   (Flutter parity — yes, GET for a mutation)
 *
 * The backend uses a path-style action verb here rather than a body. If the
 * backend later switches to POST, flip to `post(...)` and pass an empty body.
 */
export function useUnarchivePractice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (practiceId: string) =>
      get<unknown>(`/api/Practice/Unarchive/${practiceId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'practices-list'] });
    },
  });
}
